export const WHITE_LABEL_THEME_KEYS = [
  'menuBgColor',
  'menuTextColor',
  'menuIconColor',
  'menuHeaderIconColor',
  'menuHoverBgColor',
  'menuHoverTextColor',
  'menuActiveBgColor',
  'menuActiveTextColor',
  'menuActiveIndicatorColor',
  'menuDividerColor',
  'linkColor',
  'linkHoverColor',
  'buttonPrimaryBgColor',
  'buttonPrimaryTextColor',
  'buttonPrimaryHoverBgColor',
  'appTextColor',
  'headingColor',
];

/**
 * Cor de marca padrão, usada quando o whitelabel não configurou nada.
 *
 * Fica aqui, e não repetida em cada lugar, porque já divergiu: o menu caía
 * neste verde e a cor primária do tema MUI caía num azul-marinho antigo, então
 * os ícones da barra superior (que usam `palette.primary.main`) apareciam azuis
 * ao lado de um menu verde.
 */
export const DEFAULT_BRAND_COLOR = '#16803C';

export const WHITE_LABEL_THEME_DEFAULTS = {
  menuBgColor: DEFAULT_BRAND_COLOR,
  menuTextColor: '#FFFFFF',
  menuIconColor: '#FFFFFF',
  menuHeaderIconColor: '#FFFFFF',
  menuHoverBgColor: '#136E34',
  menuHoverTextColor: '#FFFFFF',
  menuActiveBgColor: '#0F5C2B',
  menuActiveTextColor: '#FFFFFF',
  menuActiveIndicatorColor: '#7DFFAF',
  menuDividerColor: '#FFFFFF',
  linkColor: '#16803C',
  linkHoverColor: '#136E34',
  buttonPrimaryBgColor: '#16803C',
  buttonPrimaryTextColor: '#FFFFFF',
  buttonPrimaryHoverBgColor: '#136E34',
  appTextColor: '#1F2937',
  headingColor: '#111827',
};

const HEX_COLOR_REGEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export const isHexColor = (value) =>
  typeof value === 'string' && HEX_COLOR_REGEX.test(value.trim());

export const normalizeHexColor = (value) => (typeof value === 'string' ? value.trim() : '');

export const normalizeColorInput = (value, fallback) => {
  if (typeof value !== 'string') return fallback;
  const trimmed = value.trim();
  if (!trimmed) return fallback;
  if (isHexColor(trimmed)) return trimmed;
  if (trimmed.startsWith('rgb(') || trimmed.startsWith('rgba(')) return trimmed;
  return fallback;
};

export const getThemeSettingValue = (settings, key) => {
  if (!settings) return undefined;
  if (Array.isArray(settings)) {
    const item = settings.find((setting) => setting.key === key);
    return item?.value;
  }
  if (typeof settings === 'object') {
    return settings[key];
  }
  return undefined;
};

export const buildWhitelabelThemeValues = ({ settings, primaryColorLight = '#16803C' } = {}) => {
  const basePrimary = normalizeColorInput(primaryColorLight, '#16803C');
  const resolved = {
    ...WHITE_LABEL_THEME_DEFAULTS,
    buttonPrimaryBgColor: basePrimary,
    linkColor: basePrimary,
  };

  WHITE_LABEL_THEME_KEYS.forEach((key) => {
    const raw = getThemeSettingValue(settings, key);
    if (typeof raw !== 'string') return;

    if (key === 'menuDividerColor') {
      const next = raw.trim();
      if (next) resolved[key] = next;
      return;
    }

    resolved[key] = normalizeColorInput(raw, resolved[key]);
  });

  const rawHeaderIcon = getThemeSettingValue(settings, 'menuHeaderIconColor');
  if (typeof rawHeaderIcon !== 'string' || !rawHeaderIcon.trim()) {
    resolved.menuHeaderIconColor = resolved.menuIconColor;
  }

  if (!isHexColor(resolved.buttonPrimaryHoverBgColor)) {
    resolved.buttonPrimaryHoverBgColor = resolved.buttonPrimaryBgColor;
  }

  return resolved;
};

const clamp = (n, a, b) => Math.min(b, Math.max(a, n));

/** @param {string} hex */
export const expandHex = (hex) => {
  const raw = normalizeHexColor(hex).replace('#', '');
  if (raw.length === 3) {
    return `#${raw
      .split('')
      .map((c) => c + c)
      .join('')}`.toUpperCase();
  }
  if (raw.length === 6) return `#${raw}`.toUpperCase();
  return '#16803C';
};

/** @param {string} hex */
export const hexToRgb = (hex) => {
  const h = expandHex(hex).replace('#', '');
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
};

/** @param {{ r: number; g: number; b: number }} rgb */
export const rgbToHex = ({ r, g, b }) => {
  const x = (n) =>
    Math.round(clamp(n, 0, 255))
      .toString(16)
      .padStart(2, '0');
  return `#${x(r)}${x(g)}${x(b)}`.toUpperCase();
};

/** @param {{ r: number; g: number; b: number }} rgb */
export const rgbToHsl = ({ r, g, b }) => {
  let R = r / 255;
  let G = g / 255;
  let B = b / 255;
  const max = Math.max(R, G, B);
  const min = Math.min(R, G, B);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  const d = max - min;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case R:
        h = ((G - B) / d + (G < B ? 6 : 0)) / 6;
        break;
      case G:
        h = ((B - R) / d + 2) / 6;
        break;
      default:
        h = ((R - G) / d + 4) / 6;
    }
  }
  return { h: h * 360, s, l };
};

/** @param {{ h: number; s: number; l: number }} hsl */
export const hslToRgb = ({ h, s, l }) => {
  const H = (((h % 360) + 360) % 360) / 360;
  const hue2rgb = (p, q, t) => {
    let T = t;
    if (T < 0) T += 1;
    if (T > 1) T -= 1;
    if (T < 1 / 6) return p + (q - p) * 6 * T;
    if (T < 1 / 2) return q;
    if (T < 2 / 3) return p + (q - p) * (2 / 3 - T) * 6;
    return p;
  };
  let r;
  let g;
  let b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, H + 1 / 3);
    g = hue2rgb(p, q, H);
    b = hue2rgb(p, q, H - 1 / 3);
  }
  return { r: r * 255, g: g * 255, b: b * 255 };
};

export const hslToHex = (hsl) => rgbToHex(hslToRgb(hsl));

/**
 * @param {string} hexA
 * @param {string} hexB
 * @param {number} t 0..1
 */
export const mixHex = (hexA, hexB, t) => {
  const a = hexToRgb(expandHex(hexA));
  const b = hexToRgb(expandHex(hexB));
  return rgbToHex({
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  });
};

/**
 * @param {string} hex
 * @param {{ dh?: number; ds?: number; dl?: number }} delta — h em graus, s/l em -1..1 somados ao valor atual
 */
export const adjustHsl = (hex, { dh = 0, ds = 0, dl = 0 } = {}) => {
  const { h, s, l } = rgbToHsl(hexToRgb(expandHex(hex)));
  return hslToHex({
    h: h + dh,
    s: clamp(s + ds, 0, 1),
    l: clamp(l + dl, 0, 1),
  });
};

export const getContrastRatio = (foreground, background) => {
  const toRgb = (hex) => {
    if (!isHexColor(hex)) return null;
    const cleanHex = hex.replace('#', '');
    const normalized =
      cleanHex.length === 3
        ? cleanHex
            .split('')
            .map((char) => char + char)
            .join('')
        : cleanHex;
    return {
      r: parseInt(normalized.slice(0, 2), 16),
      g: parseInt(normalized.slice(2, 4), 16),
      b: parseInt(normalized.slice(4, 6), 16),
    };
  };

  const toLuminance = ({ r, g, b }) => {
    const channels = [r, g, b].map((channel) => {
      const sRgb = channel / 255;
      return sRgb <= 0.03928 ? sRgb / 12.92 : ((sRgb + 0.055) / 1.055) ** 2.4;
    });
    return channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
  };

  const fg = toRgb(foreground);
  const bg = toRgb(background);
  if (!fg || !bg) return null;

  const l1 = toLuminance(fg);
  const l2 = toLuminance(bg);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
};

/** Texto legível sobre fundo (branco ou quase preto). */
export const pickContrastingText = (bgHex) => {
  const white = getContrastRatio('#FFFFFF', bgHex);
  const black = getContrastRatio('#111827', bgHex);
  if (white == null || black == null) return '#FFFFFF';
  return white >= black ? '#FFFFFF' : '#111827';
};

/**
 * Gera um conjunto harmônico de cores do whitelabel a partir de 1 a 3 cores HEX.
 * Cores faltantes são derivadas por rotação de matiz e misturas.
 *
 * @param {string[]} inputColors — até 3 cores (#RGB ou #RRGGBB)
 * @returns {Record<string, string> | null} patch com chaves de WHITE_LABEL_THEME_KEYS ou null
 */
export const generateHarmonicWhitelabelTheme = (inputColors) => {
  const valid = (Array.isArray(inputColors) ? inputColors : [])
    .map((c) => normalizeHexColor(c))
    .filter(isHexColor)
    .map(expandHex);
  if (!valid.length) return null;

  const c1 = valid[0];
  const c2 = valid[1] || adjustHsl(c1, { dh: 28 });
  const c3 = valid[2] || mixHex(c1, c2, 0.5);

  const hsl1 = rgbToHsl(hexToRgb(c1));
  const hsl2 = rgbToHsl(hexToRgb(c2));

  const menuSat = clamp(hsl1.s * 1.08, 0.35, 1);
  let menuL = hsl1.l * 0.52;
  if (hsl1.l > 0.55) menuL = clamp(hsl1.l * 0.38, 0.14, 0.28);
  menuL = clamp(menuL, 0.12, 0.34);

  const menuBgColor = hslToHex({ h: hsl1.h, s: menuSat, l: menuL });
  const menuHoverBgColor = hslToHex({ h: hsl1.h, s: menuSat, l: clamp(menuL * 0.88, 0.1, 0.32) });
  const menuActiveBgColor = hslToHex({
    h: hsl1.h,
    s: menuSat,
    l: clamp(menuL * 0.78, 0.08, 0.28),
  });

  const menuTextColor = pickContrastingText(menuBgColor);
  const menuIconColor = menuTextColor;
  const menuHoverTextColor = menuTextColor;
  const menuActiveTextColor = menuTextColor;

  const accentL = clamp(0.35 + hsl2.l * 0.45, 0.55, 0.92);
  const accentS = clamp(hsl2.s * 1.05 + 0.08, 0.45, 1);
  const menuActiveIndicatorColor = hslToHex({ h: hsl2.h, s: accentS, l: accentL });

  const menuDividerColor =
    menuTextColor === '#FFFFFF'
      ? mixHex(menuBgColor, '#FFFFFF', 0.22)
      : mixHex(menuBgColor, '#000000', 0.18);

  const brandL = clamp(hsl1.l * 0.95 + 0.06, 0.32, 0.52);
  const brandS = clamp(hsl1.s + 0.02, 0.4, 1);
  const buttonPrimaryBgColor = hslToHex({ h: hsl1.h, s: brandS, l: brandL });
  const buttonPrimaryTextColor = pickContrastingText(buttonPrimaryBgColor);
  const buttonPrimaryHoverBgColor = hslToHex({
    h: hsl1.h,
    s: brandS,
    l: clamp(brandL * 0.86, 0.12, 0.48),
  });

  const linkL = clamp(brandL + 0.06, 0.28, 0.5);
  const linkColor = hslToHex({ h: hsl1.h, s: Math.min(1, brandS + 0.06), l: linkL });
  const linkHoverColor = hslToHex({
    h: hsl1.h,
    s: Math.min(1, brandS + 0.06),
    l: clamp(linkL * 0.82, 0.15, 0.45),
  });

  const bodyTint = mixHex('#374151', c3, 0.12);
  const headingTint = mixHex('#111827', c3, 0.18);

  return {
    menuBgColor,
    menuTextColor,
    menuIconColor,
    menuHeaderIconColor: menuIconColor,
    menuHoverBgColor,
    menuHoverTextColor,
    menuActiveBgColor,
    menuActiveTextColor,
    menuActiveIndicatorColor,
    menuDividerColor,
    linkColor,
    linkHoverColor,
    buttonPrimaryBgColor,
    buttonPrimaryTextColor,
    buttonPrimaryHoverBgColor,
    appTextColor: bodyTint,
    headingColor: headingTint,
  };
};
